import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import {
  ASSET_CATEGORIES,
  ASSET_FORM_STYLES,
  CategoryChip,
  formatNumber,
  getAssetAccountingUnavailable,
  round2,
  unwrapData,
} from './assetUtils';

const EXPECTED_HEADER = 'name;kategori;anskaffningsvärde;anskaffningsdatum;nyttjandeperiod_månader;ack_avskrivningar;ack_period'
  + ' (valfritt: momsprofil;byggnadstyp;markanläggningstyp;manuell_plan)';

// Kolumnigenkänning — tolerant mot svenska/engelska rubriker och å/ä/ö-lösa
// varianter, så att en Excel-export inte fastnar på rubrikraden.
const HEADER_ALIASES = {
  name: ['name', 'namn', 'benämning', 'benamning'],
  category_code: ['kategori', 'category', 'category_code'],
  acquisition_value: ['anskaffningsvärde', 'anskaffningsvarde', 'belopp', 'acquisition_value'],
  acquisition_date: ['anskaffningsdatum', 'datum', 'acquisition_date'],
  useful_months: ['nyttjandeperiod_månader', 'nyttjandeperiod_manader', 'nyttjandeperiod', 'useful_months'],
  opening_acc_depreciation: ['ack_avskrivningar', 'ack_avskrivning', 'opening_acc_depreciation'],
  opening_period: ['ack_period', 'opening_period'],
  // K1-fakta (re-review RR3/RR4): momsbehandlingen är obligatorisk för
  // K1-fordon; klassificering krävs för K1-byggnader/markanläggningar.
  vat_profile: ['momsprofil', 'moms', 'vat_profile', 'fordonstyp'],
  k1_building_type: ['byggnadstyp', 'k1_building_type'],
  k1_land_improvement_type: ['markanläggningstyp', 'markanlaggningstyp', 'k1_land_improvement_type'],
  k1_property_manual_plan: ['manuell_plan', 'manuell_avskrivningsplan', 'k1_property_manual_plan'],
};

const normalizeHeader = (cell) => String(cell || '').replace(/^\uFEFF/, '').trim().toLowerCase();

const matchColumn = (cell) => {
  const normalized = normalizeHeader(cell);
  return Object.keys(HEADER_ALIASES).find((key) => HEADER_ALIASES[key].includes(normalized)) || null;
};

// "12 345,67" / "12345.67" → 12345.67 (svenska decimalkomman accepteras).
const parseAmount = (value) => {
  const text = String(value ?? '').replace(/\s| /g, '');
  if (!text) return NaN;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  return parseFloat(normalized);
};

const matchCategory = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return null;
  return ASSET_CATEGORIES.find(
    (c) => c.code === text || c.label.toLowerCase() === text
  ) || null;
};

// Minimal CSV-radläsare med stöd för citerade fält.
const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') { current += '"'; i += 1; } else { inQuotes = false; }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

// Exported for tests (re-review RRR5) — the CSV contract is behavior, not
// an implementation detail: momsprofil/byggnadstyp/markanläggningstyp/
// manuell_plan columns and the K1-vehicle momsprofil requirement.
export const parseCsv = (text) => {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\r|\n/)
    .filter((line) => line.trim() !== '');
  if (lines.length === 0) return { rows: [], headerError: null };

  // Avgränsare: semikolon eller komma — den som förekommer flest gånger på
  // rubrikraden vinner (svensk Excel exporterar semikolon).
  const semicolons = (lines[0].match(/;/g) || []).length;
  const commas = (lines[0].match(/,/g) || []).length;
  const delimiter = semicolons >= commas ? ';' : ',';

  const headerCells = splitCsvLine(lines[0], delimiter);
  const columns = headerCells.map(matchColumn);
  const required = ['name', 'category_code', 'acquisition_value', 'acquisition_date', 'useful_months'];
  const missing = required.filter((key) => !columns.includes(key));
  if (missing.length > 0) {
    return {
      rows: [],
      headerError: 'Rubrikraden känns inte igen. Förväntat format: ' + EXPECTED_HEADER,
    };
  }

  const rows = lines.slice(1).map((line, index) => {
    const cells = splitCsvLine(line, delimiter);
    const raw = {};
    columns.forEach((key, cellIndex) => {
      if (key) raw[key] = (cells[cellIndex] ?? '').trim();
    });

    const errors = [];
    const name = raw.name || '';
    if (!name) errors.push('Namn saknas');

    const category = matchCategory(raw.category_code);
    if (!category) {
      errors.push(`Okänd kategori "${raw.category_code || ''}" (använd ${ASSET_CATEGORIES.map((c) => c.code).join(', ')})`);
    }

    const amount = parseAmount(raw.acquisition_value);
    if (!(amount > 0)) errors.push('Anskaffningsvärdet måste vara ett belopp större än 0');

    const date = raw.acquisition_date || '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || isNaN(new Date(date).getTime())) {
      errors.push('Anskaffningsdatum måste vara ÅÅÅÅ-MM-DD');
    }

    const months = parseInt(raw.useful_months, 10);
    if (!(months > 0)) errors.push('Nyttjandeperioden måste vara ett antal månader större än 0');

    // Momsprofil: personbil | standard (lastbil/arbetsfordon räknas som
    // standard — momsen var avdragsgill). Krävs för K1-fordon, precis som
    // servern kräver (K1_VEHICLE_VAT_PROFILE_REQUIRED).
    let vatProfile = null;
    const rawVat = String(raw.vat_profile || '').trim().toLowerCase();
    if (rawVat) {
      if (rawVat === 'personbil') vatProfile = 'personbil';
      else if (['standard', 'lastbil', 'arbetsfordon'].includes(rawVat)) vatProfile = 'standard';
      else errors.push('Momsprofil måste vara personbil, lastbil eller standard');
    }
    if (category?.code === 'k1_vehicles' && !vatProfile) {
      errors.push('K1-fordon kräver momsprofil (personbil eller lastbil/standard)');
    }
    const manualPlanRaw = String(raw.k1_property_manual_plan || '').trim().toLowerCase();
    const manualPlan = ['1', 'ja', 'true', 'x'].includes(manualPlanRaw);

    const hasAcc = raw.opening_acc_depreciation !== undefined && raw.opening_acc_depreciation !== '';
    const hasPeriod = raw.opening_period !== undefined && raw.opening_period !== '';
    let openingAcc = 0;
    if (hasAcc || hasPeriod) {
      openingAcc = parseAmount(raw.opening_acc_depreciation);
      if (!(openingAcc >= 0)) errors.push('Ack. avskrivningar måste vara ett belopp');
      if (openingAcc > 0 && !/^\d{4}-\d{2}$/.test(raw.opening_period || '')) {
        errors.push('Ack. avskrivningar kräver ack_period (ÅÅÅÅ-MM)');
      }
      if (amount > 0 && openingAcc >= amount) {
        errors.push('Ack. avskrivningar måste vara mindre än anskaffningsvärdet');
      }
    }

    return {
      line: index + 2, // radnummer i filen (1-baserat, rubrikraden är rad 1)
      raw,
      errors,
      payload: errors.length === 0
        ? {
          name,
          category_code: category.code,
          acquisition_value: round2(amount),
          acquisition_date: date,
          useful_months: months,
          ...(vatProfile && { vat_profile: vatProfile }),
          ...(raw.k1_building_type && { k1_building_type: String(raw.k1_building_type).trim() }),
          ...(raw.k1_land_improvement_type && { k1_land_improvement_type: String(raw.k1_land_improvement_type).trim() }),
          ...(manualPlan && { k1_property_manual_plan: true }),
          ...(openingAcc > 0 && {
            opening_acc_depreciation: round2(openingAcc),
            opening_period: raw.opening_period,
          }),
        }
        : null,
    };
  });

  return { rows, headerError: null };
};

/**
 * AssetImportModal — CSV-import av befintliga tillgångar (t.ex. från Fortnox
 * eller Excel). All tolkning och validering sker klient-side; giltiga rader
 * skickas till POST /assets/import och serverfel visas per rad.
 */
const AssetImportModal = ({ show, onHide, companyId, onImported }) => {
  const [rawText, setRawText] = useState('');
  const [fileName, setFileName] = useState(null);
  const [serverErrors, setServerErrors] = useState({}); // previewIndex → felmeddelande
  const [submitting, setSubmitting] = useState(false);
  const [createdCount, setCreatedCount] = useState(null);

  useEffect(() => {
    if (show) {
      setRawText('');
      setFileName(null);
      setServerErrors({});
      setSubmitting(false);
      setCreatedCount(null);
    }
  }, [show]);

  const { rows, headerError } = useMemo(() => parseCsv(rawText), [rawText]);
  const validRows = rows.filter((row) => row.payload);

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setServerErrors({});
    setCreatedCount(null);
    const reader = new FileReader();
    reader.onload = () => setRawText(String(reader.result || ''));
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const handleImport = async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setServerErrors({});
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/assets/import`),
        { rows: validRows.map((row) => row.payload) },
        { withCredentials: true }
      );
      const data = unwrapData(response) || {};
      const errors = Array.isArray(data.errors) ? data.errors : [];

      if (errors.length === 0) {
        toast.success(`${data.created ?? validRows.length} tillgångar importerade — avskrivningarna sköts automatiskt.`);
        if (onImported) onImported();
        onHide();
        return;
      }

      // Serverfel: mappa tillbaka index i den skickade arrayen → previewrad.
      const mapped = {};
      errors.forEach((serverError) => {
        const submittedIndex = parseInt(serverError.row, 10);
        const previewRow = validRows[submittedIndex];
        if (previewRow) mapped[previewRow.line] = serverError.error || 'Kunde inte importeras';
      });
      setServerErrors(mapped);
      setCreatedCount(data.created ?? 0);
      if ((data.created ?? 0) > 0 && onImported) onImported();
    } catch (err) {
      const unavailable = getAssetAccountingUnavailable(err);
      toast.error(unavailable?.message || err.response?.data?.error || 'Importen misslyckades. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const rowError = (row) => serverErrors[row.line] || row.errors.join(' · ');

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="xl" centered contentClassName="asset-form">
      <Modal.Header closeButton={!submitting}>
        <Modal.Title as="h5" className="d-flex align-items-center gap-2">
          <Upload size={18} style={{ color: 'var(--primary)' }} />
          Importera tillgångar
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted" style={{ fontSize: '0.875rem' }}>
          Har du redan tillgångar i ett annat system? Ladda upp eller klistra in en
          CSV så läggs de upp med rätt avskrivningsplan. Första raden ska vara en
          rubrikrad:
        </p>
        <pre
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '0.6rem 0.9rem',
            fontSize: '0.78rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {EXPECTED_HEADER}
        </pre>
        <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
          Kategori: {ASSET_CATEGORIES.map((c) => c.code).join(', ')}. De två sista
          kolumnerna är valfria — fyll i dem om tillgången redan har skrivits av,
          så fortsätter LedgerLoop schemat därifrån istället för att börja om.
        </p>

        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <Form.Group controlId="asset-import-file" className="mb-0">
            <Form.Control type="file" accept=".csv,.txt,text/csv" onChange={handleFile} disabled={submitting} />
          </Form.Group>
          {fileName && <span className="text-muted" style={{ fontSize: '0.8125rem' }}>{fileName}</span>}
        </div>

        <Form.Group className="mb-3" controlId="asset-import-paste">
          <Form.Label style={{ fontWeight: 600 }}>… eller klistra in</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={rawText}
            onChange={(e) => { setRawText(e.target.value); setServerErrors({}); setCreatedCount(null); }}
            placeholder={`${EXPECTED_HEADER}\nMacBook Pro 14;computers;24500;2024-03-15;36;;`}
            disabled={submitting}
            style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
          />
        </Form.Group>

        {headerError && <Alert variant="warning" className="py-2">{headerError}</Alert>}

        {createdCount !== null && (
          <Alert variant="warning" className="py-2">
            {createdCount} rader importerades, men några rader avvisades av servern —
            se felen i tabellen. Rätta och importera de raderna igen.
          </Alert>
        )}

        {rows.length > 0 && (
          <>
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginBottom: 6 }}>
              {validRows.length} av {rows.length} rader kan importeras.
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
              <Table size="sm" className="mb-0 asset-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Rad</th>
                    <th>Namn</th>
                    <th>Kategori</th>
                    <th className="text-end">Anskaffningsvärde</th>
                    <th>Datum</th>
                    <th className="text-end">Nyttjandeperiod</th>
                    <th className="text-end">Ack. avskrivningar</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const failed = Boolean(rowError(row));
                    return (
                      <tr key={row.line} style={failed ? { background: '#fef2f2' } : undefined}>
                        <td className="text-muted">{row.line}</td>
                        <td>{row.raw.name || '—'}</td>
                        <td>
                          {matchCategory(row.raw.category_code) ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <CategoryChip code={matchCategory(row.raw.category_code).code} size={22} iconSize={12} tone="neutral" />
                              {matchCategory(row.raw.category_code).label}
                            </span>
                          ) : (
                            row.raw.category_code || '—'
                          )}
                        </td>
                        <td className="text-end asset-num">
                          {row.payload ? `${formatNumber(row.payload.acquisition_value)} kr` : row.raw.acquisition_value || '—'}
                        </td>
                        <td>{row.raw.acquisition_date || '—'}</td>
                        <td className="text-end asset-num">{row.raw.useful_months ? `${row.raw.useful_months} mån` : '—'}</td>
                        <td className="text-end asset-num">
                          {row.payload?.opening_acc_depreciation
                            ? `${formatNumber(row.payload.opening_acc_depreciation)} kr (t.o.m. ${row.payload.opening_period})`
                            : row.raw.opening_acc_depreciation || '—'}
                        </td>
                        <td style={{ maxWidth: 260 }}>
                          {failed ? (
                            <span style={{ color: '#b91c1c', fontSize: '0.78rem' }}>{rowError(row)}</span>
                          ) : (
                            <span style={{ color: '#166534', fontSize: '0.78rem', fontWeight: 600 }}>OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
          Avbryt
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={submitting || validRows.length === 0}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Importerar…
            </>
          ) : (
            `Importera ${validRows.length} ${validRows.length === 1 ? 'tillgång' : 'tillgångar'}`
          )}
        </Button>
      </Modal.Footer>
      <style>{ASSET_FORM_STYLES}</style>
    </Modal>
  );
};

export default AssetImportModal;
